import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import OrganizerLayout from '../../components/layouts/OrganizerLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  Users, Calendar, CreditCard, FileText, Bell, 
  TrendingUp, Clock, CheckCircle2, AlertCircle,
  ArrowRight, Loader2, PartyPopper, Copy, Check, Crown, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import CountdownWidget from '../../components/CountdownWidget';
import { useSubscription } from '../../context/SubscriptionContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizerDashboard = () => {
  const { user } = useAuth();
  const { currentTier } = useSubscription();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchDashboardData(selectedEvent.id);
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`);
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEvent(response.data[0]);
      } else {
        setLoading(false);
      }
    } catch (error) {
      toast.error('Failed to load events');
      setLoading(false);
    }
  };

  const fetchDashboardData = async (eventId) => {
    try {
      const [statsRes, announcementsRes] = await Promise.all([
        axios.get(`${API_URL}/api/event/stats?event_id=${eventId}`),
        axios.get(`${API_URL}/api/event/announcements?event_id=${eventId}`)
      ]);
      setStats(statsRes.data);
      setAnnouncements(announcementsRes.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyEventCode = () => {
    if (selectedEvent) {
      navigator.clipboard.writeText(selectedEvent.code);
      setCopiedCode(true);
      toast.success('Event code copied!');
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const statCards = [
    { 
      title: 'Total Attendees', 
      value: stats?.total_attendees || 0, 
      icon: Users, 
      color: 'bg-maroon',
      link: '/organizer/attendees'
    },
    { 
      title: 'Committees', 
      value: stats?.total_committees || 0, 
      icon: Users, 
      color: 'bg-blue-500',
      link: '/organizer/committees'
    },
    { 
      title: 'Pending Payments', 
      value: stats?.pending_payments || 0, 
      icon: Clock, 
      color: 'bg-amber-500',
      link: '/organizer/payments'
    },
    { 
      title: 'Verified Payments', 
      value: stats?.verified_payments || 0, 
      icon: CheckCircle2, 
      color: 'bg-green-500',
      link: '/organizer/payments'
    },
  ];

  if (loading) {
    return (
      <OrganizerLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-maroon" />
        </div>
      </OrganizerLayout>
    );
  }

  // No events yet - prompt to create one
  if (events.length === 0) {
    return (
      <OrganizerLayout>
        <div className="space-y-8" data-testid="organizer-dashboard">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <div className="text-center py-8">
              <PartyPopper className="h-16 w-16 mx-auto mb-4 text-maroon" />
              <h1 className="font-heading text-3xl font-bold text-maroon mb-2">
                Welcome, {user?.first_name}!
              </h1>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Get started by creating your first reunion event. Once created, you'll get a unique code to share with your batchmates.
              </p>
              <Button 
                onClick={() => navigate('/organizer/events')}
                className="bg-maroon hover:bg-maroon-hover text-white rounded-full"
                data-testid="create-first-event-btn"
              >
                <PartyPopper className="h-4 w-4 mr-2" />
                Create Your First Event
              </Button>
            </div>
          </div>
        </div>
      </OrganizerLayout>
    );
  }

  return (
    <OrganizerLayout>
      <div className="space-y-8" data-testid="organizer-dashboard">
        {/* Welcome Section with Event Selector */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="font-heading text-3xl font-bold text-maroon">
                Welcome, {user?.first_name}!
              </h1>
              <p className="text-gray-600 mt-2">
                Managing: <span className="font-semibold">{selectedEvent?.school_name}</span>
              </p>
              <p className="text-maroon font-medium">{selectedEvent?.event_name}</p>
            </div>
            
            {/* Event Code Card */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="bg-gradient-to-br from-maroon to-maroon-hover p-6 rounded-2xl text-white">
                <p className="text-xs uppercase tracking-wider text-white/70 mb-1">Event Code</p>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-2xl font-bold tracking-widest" data-testid="dashboard-event-code">
                    {selectedEvent?.code}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="bg-white/20 hover:bg-white/30 text-white border-0"
                    onClick={copyEventCode}
                    data-testid="copy-dashboard-code"
                  >
                    {copiedCode ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-white/60 mt-2">Share this with attendees</p>
              </div>
              
              {/* Subscription Plan Card */}
              <div className="bg-gradient-to-br from-gold/90 to-gold p-6 rounded-2xl text-cream-dark" data-testid="subscription-plan-card">
                <p className="text-xs uppercase tracking-wider text-cream-dark/60 mb-1">Current Plan</p>
                <div className="flex items-center gap-2">
                  <Crown className="h-5 w-5" />
                  <span className="text-xl font-bold capitalize" data-testid="current-plan-name">
                    {currentTier === 'none' ? 'Free' : currentTier}
                  </span>
                </div>
                {currentTier !== 'license' && (
                  <Button
                    size="sm"
                    className="mt-3 bg-cream-dark hover:bg-maroon text-white text-xs w-full"
                    onClick={() => navigate('/organizer/subscription')}
                    data-testid="upgrade-plan-btn"
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    Upgrade Plan
                  </Button>
                )}
                {currentTier === 'license' && (
                  <p className="text-xs text-cream-dark/60 mt-2">Full access unlocked</p>
                )}
              </div>
            </div>
          </div>

          {/* Event Switcher (if multiple events) */}
          {events.length > 1 && (
            <div className="mt-6 pt-6 border-t">
              <p className="text-sm text-gray-500 mb-2">Switch Event:</p>
              <div className="flex flex-wrap gap-2">
                {events.map(event => (
                  <Button
                    key={event.id}
                    variant={selectedEvent?.id === event.id ? "default" : "outline"}
                    size="sm"
                    className={selectedEvent?.id === event.id ? "bg-maroon hover:bg-maroon-hover" : ""}
                    onClick={() => setSelectedEvent(event)}
                  >
                    {event.event_name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Event Countdown */}
        <CountdownWidget eventDate={selectedEvent?.event_date} eventName={selectedEvent?.event_name} />

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <Link key={stat.title} to={stat.link} className="group">
              <Card className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 group-hover:-translate-y-1 bg-white">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-500">{stat.title}</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-xl ${stat.color}`}>
                      <stat.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Collection Summary */}
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-maroon" />
                Collection Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6">
                <p className="text-sm text-gray-500 mb-2">Total Collected</p>
                <p className="text-4xl font-bold text-maroon">
                  ₱{(stats?.total_collected || 0).toLocaleString()}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">{stats?.pending_payments || 0}</p>
                  <p className="text-sm text-gray-500">Pending</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{stats?.verified_payments || 0}</p>
                  <p className="text-sm text-gray-500">Verified</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Announcements */}
          <Card className="border-0 shadow-sm bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Bell className="h-5 w-5 text-maroon" />
                  Recent Announcements
                </CardTitle>
              </div>
              <Link to="/organizer/announcements">
                <Button variant="ghost" size="sm" className="text-maroon">
                  View All <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {announcements.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No announcements yet</p>
                  <Link to="/organizer/announcements">
                    <Button variant="link" className="text-maroon mt-2">
                      Create your first announcement
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {announcements.map((ann) => (
                    <div key={ann.id} className="p-3 bg-cream-paper rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">
                          {ann.priority}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {ann.acknowledged_by?.length || 0} acknowledged
                        </span>
                      </div>
                      <p className="font-medium text-gray-900 truncate">{ann.title}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="border-0 shadow-sm bg-white">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/organizer/announcements">
                <Button variant="outline" className="w-full h-20 flex flex-col gap-2 hover:bg-cream-paper hover:border-maroon">
                  <Bell className="h-6 w-6 text-maroon" />
                  <span className="text-sm">Post Announcement</span>
                </Button>
              </Link>
              <Link to="/organizer/payments">
                <Button variant="outline" className="w-full h-20 flex flex-col gap-2 hover:bg-cream-paper hover:border-maroon">
                  <CreditCard className="h-6 w-6 text-maroon" />
                  <span className="text-sm">Review Payments</span>
                </Button>
              </Link>
              <Link to="/organizer/documents">
                <Button variant="outline" className="w-full h-20 flex flex-col gap-2 hover:bg-cream-paper hover:border-maroon">
                  <FileText className="h-6 w-6 text-maroon" />
                  <span className="text-sm">Upload Document</span>
                </Button>
              </Link>
              <Link to="/organizer/events">
                <Button variant="outline" className="w-full h-20 flex flex-col gap-2 hover:bg-cream-paper hover:border-maroon">
                  <PartyPopper className="h-6 w-6 text-maroon" />
                  <span className="text-sm">Manage Events</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </OrganizerLayout>
  );
};

export default OrganizerDashboard;
